import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import numeral from 'numeral';

import { Helmet } from 'react-helmet';
import DataTable from "react-data-table-component";
import TableLoader from '../TableLoader';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import SectionHeader from '../SectionHeader';
import FilterSelect from '../FilterSelect';
import Modal from '../Modal';
import Loader from '../Loader';

import DesignAndEngineeringProjectForm from './DesignAndEngineeringProjectForm';
import DesignAndEngineeringItemsForm from './DesignAndEngineeringItemsForm';
import ProjectQuoteView from './ProjectQuoteView';

import useDesignAndEngineeringProjects from "../../hooks/useDesignAndEngineeringProjects";
import useDesignAndEngineeringProjectClients from "../../hooks/useDesignAndEngineeringProjectClients";
import useDebounce from "../../hooks/useDebounce";

import designAndEngineeringProjectService from '../../services/designAndEngineeringProjectService';

import { formatDateToSydneyTimeZone } from '../../utils/dateUtils';

import designAndEngineeringProjectStatuses from '../../data/designAndEngineeringProjectStatuses.json';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const engineeringMargin = (item) => {
  return item.engineering * item.marginPercentage / 100;
}

const engineeringTotal = (item) => {
  return item.engineering + engineeringMargin(item);
}

const designMargin = (item) => {
  return item.design * item.marginPercentage / 100;
};

const designTotal = (item) => {
  return item.design + designMargin(item);
};

const consultMargin = (item) => {
  if (item.consultMargin) {
    return item.consult * item.marginPercentage / 100;
  }

  return 0;
};

const consultTotal = (item) => {
  return item.consult + consultMargin(item);
}

const DrawingItems = ({ data }) => {
  const filteredItems = data.items.filter(item => item.accepted);

  const engineeringWithoutMarginTotal = filteredItems.reduce((acc, item) => acc + item.engineering, 0);
  const designWithoutMarginTotal = filteredItems.reduce((acc, item) => acc + item.design, 0);
  const consultWithoutMarginTotal = filteredItems.reduce((acc, item) => acc + item.consult, 0);

  const total = filteredItems.reduce((acc, item) => {
    return acc + item.engineering + item.design + item.consult;
  }, 0);

  const designWithMarginTotal = filteredItems.reduce((acc, item) => {
    return acc + designTotal(item);
  }, 0);

  const engineeringWithMarginTotal = filteredItems.reduce((acc, item) => {
    return acc + engineeringTotal(item);
  }, 0);

  const consultWithMarginTotal = filteredItems.reduce((acc, item) => {
    return acc + consultTotal(item);
  }, 0);

  const totalWithMargin = designWithMarginTotal + engineeringWithMarginTotal + consultWithMarginTotal;

  const aseMargin = totalWithMargin - total;

  const userCompanyName = (project, userId) => {
    if (project.engineeringCompany.staff.includes(userId)) return project.engineeringCompany.name;
    if (project.designCompany.staff.includes(userId)) return project.designCompany.name;
  }

  return (
    <div className='border-t border-b border-ase-blue max-w-full'>
      {data.assignedTo && data.assignedTo.length > 0 && (
        <p className="p-4 text-sm border-b border-[#0000001f]">
          <span className='font-bold'>Assigned to:</span> {data.assignedTo.map(u => `${u.fullName} (${userCompanyName(data, u._id)})`).join(', ')}
        </p>
      )}

      {data.items.length > 0 && (
        <div className='grid grid-cols-9 text-white bg-ase-blue p-4 text-xs md:text-sm'>
          <div className='flex flex-col gap-2 items-center'>
            <p>Engineering</p>
            <p className='font-medium'>
              {numeral(engineeringWithoutMarginTotal).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>Design</p>
            <p className='font-medium'>
              {numeral(designWithoutMarginTotal).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>Consult</p>
            <p className='font-medium'>
              {numeral(consultWithoutMarginTotal).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>Total</p>
            <p className='font-medium'>
              {numeral(total).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>ASE Engineering</p>
            <p className='font-medium'>
              {numeral(engineeringWithMarginTotal).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>ASE Design</p>
            <p className='font-medium'>
              {numeral(designWithMarginTotal).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>ASE Consult</p>
            <p className='font-medium'>
              {numeral(consultWithMarginTotal).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>ASE Total</p>
            <p className='font-medium'>
              {numeral(totalWithMargin).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col gap-2 items-center'>
            <p>ASE Margin</p>
            <p className='font-medium'>
              {numeral(aseMargin).format('$0,0.00')}
            </p>
          </div>
        </div>
      )}

      {data.items.length > 0 ? (
        <div>
          {data.items.map((item, index) => (
            <div key={`project-${data._id}-item-${index}`}>
              <div className='flex items-center justify-between gap-2 text-sm border-b border-[#0000001f]'>
                <div className='flex items-center gap-4'>
                  <div className='px-4 font-bold'>
                    <p>#{index + 1}</p>
                  </div>

                  <div className='flex flex-col gap-2 px-4 py-4 whitespace-pre-line'>
                    <p>{item.name}</p>

                    {<p>Accepted: {item.accepted ? <span className='text-green-600'>Yes</span> : <span className='text-red-600'>No</span>}</p>}
                  </div>
                </div>

                <div className='grid grid-cols-3 gap-4 px-4 w-[300px] min-w-[300px] py-4'>
                  <p className='font-medium'>Engineering</p>
                  <p>{numeral(item.engineering).format('$0,0.00')}</p>
                  <p className='text-ase-blue font-semibold'>{numeral(engineeringTotal(item)).format('$0,0.00')}</p>

                  <p className='font-medium'>Design</p>
                  <p>{numeral(item.design).format('$0,0.00')}</p>
                  <p className='text-ase-blue font-semibold'>{numeral(designTotal(item)).format('$0,0.00')}</p>

                  {item.consult > 0 && (
                    <>
                      <p>Consult</p>
                      <p>{numeral(item.consult).format('$0,0.00')}</p>
                      <p className='text-ase-blue font-semibold'>{numeral(consultTotal(item)).format('$0,0.00')}</p>
                    </>
                  )}

                  <p className='font-medium'>Total</p>
                  <p>{numeral(item.engineering + item.design + item.consult).format('$0,0.00')}</p>
                  <p className='text-ase-blue font-semibold'>{numeral(engineeringTotal(item) + designTotal(item) + consultTotal(item)).format('$0,0.00')}</p>

                  <p className='font-medium'>Margin</p>
                  <p>{numeral(item.marginPercentage).format('0.00')}%</p>
                  <p className='text-ase-blue font-semibold'>{numeral(engineeringMargin(item) + designMargin(item) + consultMargin(item)).format('$0,0.00')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='flex items-center justify-center h-[72px]'>
          <p className='text-sm'>There are no records to display</p>
        </div>
      )}
    </div>
  )
}

const DesignAndEngineeringProjects = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);

  const [selectedStatus, setSelectedStatus] = useState(null);

  useEffect(() => { setPage(1); }, [search, selectedStatus, dateRange]);

  const [selectedProject, setSelectedProject] = useState(null);

  const [isProjectFormOpen, setIsProjectFormOpen] = useState(false);
  const [isItemsFormOpen, setIsItemsFormOpen] = useState(false);
  const [isImportingProjects, setIsImportingProjects] = useState(false);

  const [isQuoteViewOpen, setisQuoteViewOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data,
    isLoading: isLoadingDesignAndEngineeringProjects,
    mutate
  } = useDesignAndEngineeringProjects({
    status: selectedStatus?.value || null,
    startDate: dateRange?.[0] || null,
    endDate: dateRange?.[1] || null,
    search,
    sortField,
    sortOrder,
    page,
    perPage
  });

  const designAndEngineeringProjects = data?.designAndEngineeringProjects;
  const designAndEngineeringProjectCount = data?.count;

  const {
    data: designAndEngineeringProjectClients,
    mutate: mutateDrawingClients
  } = useDesignAndEngineeringProjectClients();

  const clients = designAndEngineeringProjectClients?.clients;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleRowClicked = (project) => {
    setSelectedProject(project);
    setIsProjectFormOpen(true);
  }

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const handleDrawingFormSubmit = async (payload) => {
    if (selectedProject) {
      setIsSubmitting(true);

      console.log('payload: ', payload);

      const { designAndEngineeringProject } = await designAndEngineeringProjectService.updateDesignAndEngineeringProject({
        designAndEngineeringProjectId: selectedProject._id,
        updateData: payload
      });

      await mutate();
      await mutateDrawingClients();

      setIsSubmitting(false);
      setIsProjectFormOpen(false);
      setSelectedProject(null);

      toast.success(`Project ${designAndEngineeringProject.aseId} updated`);
    } else {
      setIsSubmitting(true);

      const { designAndEngineeringProject } = await designAndEngineeringProjectService.createDesignAndEngineeringProject({ ...payload });

      await mutate();
      await mutateDrawingClients();

      setIsSubmitting(false);
      setIsProjectFormOpen(false);

      toast.success(`Project ${designAndEngineeringProject.aseId} created`);
    }
  }

  const handleItemsFormSubmit = async (items) => {
    setIsSubmitting(true);

    const { designAndEngineeringProject } = await designAndEngineeringProjectService.updateDesignAndEngineeringProject({
      designAndEngineeringProjectId: selectedProject._id,
      updateData: { items }
    });

    await mutate();
    await mutateDrawingClients();

    setIsSubmitting(false);
    setIsItemsFormOpen(false);
    setSelectedProject(null);

    toast.success(`Drawing ${designAndEngineeringProject.aseId} updated`);
  }

  const handleEditItemsClicked = (project) => {
    setSelectedProject(project);
    setIsItemsFormOpen(true);
  }

  const handleQuoteProjectClicked = (project) => {
    setSelectedProject(project);
    setisQuoteViewOpen(true);
  }

  const handleCloseDrawingForm = () => {
    setSelectedProject(null);
    setIsProjectFormOpen(false);
  }

  const handleCloseItemsForm = () => {
    setSelectedProject(null);
    setIsItemsFormOpen(false);
  }

  const handleCloseQuoteView = () => {
    setisQuoteViewOpen(false);
    setSelectedProject(null);
  }

  const importProjects = async () => {
    setIsImportingProjects(true);

    try {
      await designAndEngineeringProjectService.importDesignAndEngineeringProjects();

      toast.success('Projects imported successfully');
    } catch (error) {
      console.error('Error importing projects:', error);
      toast.error('Failed to import projects. Please try again later');
    } finally {
      setIsImportingProjects(false);
    }
  }

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      sortField: 'name'
    },
    {
      name: 'Quote #',
      selector: row => row.aseId,
      sortable: true,
      sortField: 'aseId'
    },
    {
      name: 'Client',
      selector: row => row.client,
      sortable: true,
      sortField: 'client'
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      sortField: 'status',
      conditionalCellStyles: [
        {
          when: row => row.status === 'Lost',
          classNames: ['text-red-600']
        },
        {
          when: row => row.status === 'Pending',
          classNames: ['text-yellow-600']
        },
        {
          when: row => row.status === 'In progress',
          classNames: ['text-ase-blue']
        },
        {
          when: row => row.status === 'Completed',
          classNames: ['text-green-600']
        }
      ]
    },
    {
      name: 'Start date',
      selector: row => row.startDate,
      format: row => formatDateToSydneyTimeZone(row.startDate),
      sortable: true,
      sortField: 'startDate'
    },
    {
      name: 'End date',
      selector: row => row.endDate,
      format: row => formatDateToSydneyTimeZone(row.endDate),
      sortable: true,
      sortField: 'endDate'
    },
    {
      name: 'PO #',
      selector: row => row.poNumber,
      sortable: true,
      sortField: 'poNumber'
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleEditItemsClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Items
          </button>

          <button
            onClick={() => handleQuoteProjectClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Quote
          </button>
        </div>
      )
    }
  ];

  const handleQuoteCreated = async () => {
    await mutate();
  };

  return (
    <>
      <Helmet>
        <title>D & E Projects | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Projects'
          actionsLeft={
            <>
              <button
                onClick={() => setIsProjectFormOpen(true)}
                className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
              >
                Add
              </button>

              <button
                onClick={importProjects}
                className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
              >
                Import
              </button>
            </>
          }
          actionsRight={(
            <div className='flex flex-col md:flex-row md:items-center gap-2 w-full'>
              <div className="relative flex items-center">
                <MagnifyingGlassIcon className="absolute left-[10px] h-3 w-3 text-gray-600 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name, quote # or client..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="!pl-[28px] pr-2 text-xs !h-[25px] !border border-none rounded-md bg-white text-gray-700 placeholder-gray-400 focus:outline-none w-52"
                />
              </div>

              <p className='text-white text-xs'>Status:</p>
              <div className='text-xs h-[24px]'>
                <FilterSelect
                  options={designAndEngineeringProjectStatuses}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder='Select status'
                />
              </div>

              <p className='text-white text-xs'>Period:</p>

              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format='dd/MM/y'
                className="filter-date-range"
              />
            </div>
          )}
        />

        <DataTable
          columns={columns}
          data={designAndEngineeringProjects}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={handleRowClicked}
          expandableRows
          expandableRowsComponent={DrawingItems}
          progressPending={isLoadingDesignAndEngineeringProjects}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={designAndEngineeringProjectCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </div>

      <Modal isOpen={isQuoteViewOpen} overflowType='visible'>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[600px] pb-1 overflow-visible">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>{selectedProject?.name} quote</p>

            <p
              onClick={handleCloseQuoteView}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <div className="flex items-center gap-6 px-4 py-4">
            <ProjectQuoteView
              project={selectedProject}
              isLoading={isSubmitting}
              onQuoteCreated={handleQuoteCreated}
              onCloseClicked={handleCloseQuoteView}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={isProjectFormOpen} overflowType='visible'>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[600px] pb-1 overflow-visible">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>{selectedProject ? 'Update' : 'Add'} project</p>

            <p
              onClick={handleCloseDrawingForm}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <div className="flex items-center gap-6 px-4 py-4">
            <DesignAndEngineeringProjectForm
              project={selectedProject}
              clients={clients}
              isLoading={isSubmitting}
              onSubmit={handleDrawingFormSubmit}
              onCloseClicked={handleCloseDrawingForm}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={isItemsFormOpen}>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>Project items</p>

            <p
              onClick={handleCloseItemsForm}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <div className="flex items-center gap-6">
            <DesignAndEngineeringItemsForm
              project={selectedProject}
              isLoading={isSubmitting}
              onSubmit={handleItemsFormSubmit}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={isImportingProjects}>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>Importing projects</p>

            <p
              onClick={() => setIsImportingProjects(false)}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <div className="flex flex-col items-center gap-4 p-4">
            <Loader />
            <p>Importing projects, this can take a minute.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default DesignAndEngineeringProjects;
